import { defineStore } from 'pinia'
import type { Organization } from '@/models/organization'
import organizationService, {
  type CreateOrganizationParams,
  type UpdateOrganizationParams,
} from '@/services/organizationService'
import type { UserReport } from '@/models/UserReport'

interface OrganizationState {
  currentOrganization: Organization | null
  availableOrganizations: Organization[]
  isLoading: boolean
  error: string | null
}

export const useOrganizationStore = defineStore('organization', {
  state: (): OrganizationState => ({
    currentOrganization: JSON.parse(localStorage.getItem('currentOrganization') || 'null'),
    availableOrganizations: [],
    isLoading: false,
    error: null,
  }),

  getters: {
    hasOrganization: (state): boolean => !!state.currentOrganization,
    isOrganizationApproved: (state): boolean => state.currentOrganization?.status === 'approved',
    /**
     * The CRM integration this organization has on, if any. Only one can be active
     * at a time (the backend enforces it); each CRM card blocks while another is on.
     */
    activeCrm: (state): 'HubSpot' | 'Pipedrive' | 'onOffice' | null => {
      const org = state.currentOrganization
      if (org?.hubspot_enabled) return 'HubSpot'
      if (org?.pipedrive_enabled) return 'Pipedrive'
      if (org?.onoffice_enabled) return 'onOffice'
      return null
    },
  },

  actions: {
    async fetchCurrentOrganization() {
      try {
        this.isLoading = true
        this.error = null

        // Check if we have a forced organization selection
        const forceOrgId = localStorage.getItem('forceSelectOrganization')
        if (forceOrgId) {
          console.log('Found forced organization selection:', forceOrgId)
          // Clear it so it's only used once
          localStorage.removeItem('forceSelectOrganization')

          // If we already have this org in state, keep using it
          if (this.currentOrganization?.id === forceOrgId) {
            console.log('Already selected, preserving current organization')
            return
          }
        }

        const savedOrgId = forceOrgId || this.currentOrganization?.id
        const savedOrg = this.currentOrganization ? { ...this.currentOrganization } : null

        const organizations = await organizationService.getCurrentOrganization()

        if (organizations && organizations.length > 0) {
          if (savedOrgId) {
            const freshSelectedOrg = organizations.find((org) => org.id === savedOrgId)

            if (freshSelectedOrg) {
              this.currentOrganization = freshSelectedOrg
            } else if (forceOrgId) {
              // If we're forcing a specific org that's not yet available in the API response,
              // keep the current selection intact
              console.log('Forced organization not found in response, keeping current selection')
              // Actually keep the saved organization
              this.currentOrganization = savedOrg
            } else {
              console.log('Organization not found in response, keeping current selection')
              this.currentOrganization = savedOrg

              if (!this.currentOrganization) {
                this.currentOrganization = organizations[0]
              }
            }
          } else {
            this.currentOrganization = organizations[0]
          }
        } else {
          this.currentOrganization = null
        }

        localStorage.setItem('currentOrganization', JSON.stringify(this.currentOrganization))
      } catch (error) {
        this.error = 'Failed to fetch organization'
        console.error('Error fetching organization:', error)
      } finally {
        this.isLoading = false
      }
    },

    async createOrganization(params: CreateOrganizationParams) {
      try {
        this.isLoading = true
        this.error = null

        const organization = await organizationService.create(params)
        console.log('Created organization:', organization)

        if (organization) {
          this.currentOrganization = organization
          localStorage.setItem('currentOrganization', JSON.stringify(organization))
        }
        /*

        try {
          const organizations = await organizationService.getCurrentOrganization()

          if (organizations && organizations.length > 0) {
            const newOrg = organizations.find((org) => org.id === organization.id)

            if (newOrg) {
              console.log('Found organization in response, updating with fresh data')
              this.currentOrganization = newOrg
              localStorage.setItem('currentOrganization', JSON.stringify(newOrg))
            }
          }
        } catch (fetchError) {
          console.warn(
            'Failed to fetch updated organization data, using direct response',
            fetchError,
          )
        }
*/
        return organization
      } catch (error) {
        this.error = 'Failed to create organization'
        console.error('Error creating organization:', error)
        throw error
      } finally {
        this.isLoading = false
      }
    },

    async updateOrganization(updates: UpdateOrganizationParams) {
      if (!this.currentOrganization) {
        throw new Error('No organization selected')
      }

      try {
        this.isLoading = true
        this.error = null

        const updated = await organizationService.update(this.currentOrganization.id, updates)
        // The update response doesn't include the per-user `my_role` field, so preserve
        // it from the current org to avoid temporarily losing admin status until refresh.
        this.currentOrganization = {
          ...updated,
          my_role: updated.my_role ?? this.currentOrganization.my_role,
        }
        localStorage.setItem('currentOrganization', JSON.stringify(this.currentOrganization))
        return updated
      } catch (error) {
        this.error = 'Failed to update organization'
        console.error('Error updating organization:', error)
        throw error
      } finally {
        this.isLoading = false
      }
    },

    /** Reflect a change the backend made elsewhere (e.g. connecting an integration). */
    patchCurrentOrganization(fields: Partial<Organization>) {
      if (!this.currentOrganization) return
      this.currentOrganization = { ...this.currentOrganization, ...fields }
      localStorage.setItem('currentOrganization', JSON.stringify(this.currentOrganization))
    },

    clearOrganization() {
      this.currentOrganization = null
      this.error = null
      localStorage.removeItem('currentOrganization')
    },

    async searchAllUsers(query: string) {
      try {
        return await organizationService.searchAllUsers(query)
      } catch (error) {
        console.error('Error searching users:', error)
        throw error
      }
    },

    async inviteProfile(profileId: string, email: string, skipSeats: boolean = false) {
      try {
        await organizationService.inviteProfileToOrganization(
          profileId,
          this.currentOrganization!.id,
          email,
          skipSeats,
        )
      } catch (error) {
        console.error('Error inviting user:', error)
        throw error
      }
    },

    async getMembers() {
      try {
        return await organizationService.getOrganizationMembers(this.currentOrganization!.id)
      } catch (error) {
        console.error('Error fetching members:', error)
        throw error
      }
    },
    async inviteAdmin(email: string) {
      try {
        await organizationService.inviteAdmin(this.currentOrganization!.id, email)
      } catch (error) {
        console.error('Error inviting admin:', error)
        throw error
      }
    },

    async fetchAvailableOrganizations(admin: boolean = false) {
      try {
        this.isLoading = true
        this.error = null

        const organizations = admin
          ? await organizationService.getAllOrganizations()
          : await organizationService.getCurrentOrganization()
        this.availableOrganizations = organizations
        return organizations
      } catch (error) {
        this.error = 'Failed to fetch organizations'
        console.error('Error fetching organizations:', error)
        return []
      } finally {
        this.isLoading = false
      }
    },

    setCurrentOrganization(organization: Organization) {
      this.currentOrganization = organization
      localStorage.setItem('currentOrganization', JSON.stringify(this.currentOrganization))

      window.dispatchEvent(
        new CustomEvent('organization-changed', {
          detail: { organizationId: organization.id },
        }),
      )
    },

    async inviteByPhoneNumber(
      phoneNumber: string,
      email: string,
      language: string = 'en',
      skipSeats: boolean = false,
    ) {
      try {
        await organizationService.inviteByPhoneNumber(
          this.currentOrganization!.id,
          phoneNumber,
          email,
          language,
          skipSeats,
        )
      } catch (error) {
        console.error('Error inviting by phone number:', error)
        throw error
      }
    },

    async checkExistingPhoneNumbers(phoneNumbers: string[]): Promise<string[]> {
      try {
        return await organizationService.checkExistingPhoneNumbers(
          this.currentOrganization!.id,
          phoneNumbers,
        )
      } catch (error) {
        console.error('Error checking existing phone numbers:', error)
        throw error
      }
    },

    async bulkImportUsers(
      members: Map<string, string>,
      method: 'replace' | 'update',
      language: string = 'en',
    ) {
      try {
        await organizationService.bulkImportUsers(
          this.currentOrganization!.id,
          members,
          method,
          language,
        )
      } catch (error) {
        console.error('Error during bulk import:', error)
        throw error
      }
    },
    async removeMember(membershipId: string, type: string) {
      try {
        await organizationService.removeMember(membershipId, type)
      } catch (error) {
        console.error('Error removing member:', error)
        throw error
      }
    },

    async reinviteMember(membershipId: string, type: string) {
      try {
        await organizationService.reinviteMember(membershipId, type)
      } catch (error) {
        console.error('Error reinviting member:', error)
        throw error
      }
    },

    async updateMember(
      membershipId: string,
      updates: {
        first_name?: string
        last_name?: string
        email?: string
        job_title?: string
        role?: 'admin' | 'admin_only' | 'member'
      },
    ) {
      try {
        return await organizationService.updateMember(membershipId, updates)
      } catch (error) {
        console.error('Error updating member:', error)
        throw error
      }
    },

    async getBundles(): Promise<string> {
      try {
        return await organizationService.getBundles(this.currentOrganization!.id)
      } catch (error) {
        console.error('Error fetching bundles:', error)
        throw error
      }
    },

    async getRemainingMessages(): Promise<number> {
      try {
        return await organizationService.getRemainingMessages(this.currentOrganization!.id)
      } catch (error) {
        console.error('Error fetching remaining messages:', error)
        throw error
      }
    },

    async getReportedUsers(): Promise<UserReport[]> {
      try {
        return await organizationService.getReportedUsers()
      } catch (error) {
        console.error('Error fetching reported users:', error)
        throw error
      }
    },

    async updateReportStatus(
      reportId: number,
      status: 'pending' | 'reviewed' | 'resolved' | 'dismissed',
    ): Promise<UserReport> {
      try {
        return await organizationService.updateReportStatus(reportId, status)
      } catch (error) {
        console.error('Error updating report status:', error)
        throw error
      }
    },

    async banUser(profileId: string, reportId: number): Promise<void> {
      try {
        await organizationService.banUser(profileId, reportId)
      } catch (error) {
        console.error('Error banning user:', error)
        throw error
      }
    },
  },
})
